import React, { useEffect, useState } from 'react';
import { Loader2Icon, AlertCircleIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Restaurant } from './types';
import { InventoryDashboard } from './InventoryDashboard';
import { IngredientsTable } from './IngredientsTable';
import { RecipesTable } from './RecipesTable';
import { ProfitabilityTable } from './ProfitabilityTable';
import { AlertsPanel } from './AlertsPanel';
type SubTab = 'resumen' | 'ingredientes' | 'recetas' | 'rentabilidad' | 'alertas';
export function InventoryPanel() {
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SubTab>('resumen');
  useEffect(() => {
    async function resolveRestaurant() {
      try {
        setLoading(true);
        const currentDomain = window.location.hostname;
        // Find restaurant via restaurant_domains table
        let {
          data: domainRecord,
          error
        } = await supabase.from('restaurant_domains').select('restaurant_id, restaurants(*)').eq('domain', currentDomain).limit(1).single();
        // Fallback for local development if domain not found
        if (error && (currentDomain === 'localhost' || currentDomain.includes('magicpatterns'))) {
          console.warn('Domain not found, falling back to first restaurant for development');
          const fallback = await supabase.from('restaurant_domains').select('restaurant_id, restaurants(*)').limit(1).single();
          if (fallback.data) {
            domainRecord = fallback.data;
            error = null;
          }
        }
        if (error) throw error;
        const restaurantData = (domainRecord as any)?.restaurants;
        if (!restaurantData) throw new Error('Restaurante no encontrado para este dominio');
        setRestaurant(restaurantData);
      } catch (err: any) {
        console.error('Error resolving restaurant:', err);
        setError(err.message || 'Error de conexión');
      } finally {
        setLoading(false);
      }
    }
    resolveRestaurant();
  }, []);
  if (loading) {
    return <div className="flex flex-col items-center justify-center py-20" data-id="element-300">
        <Loader2Icon className="w-10 h-10 text-dorado animate-spin mb-4" data-id="element-301" />
        <p className="text-gray-400" data-id="element-302">Cargando sistema de inventario...</p>
      </div>;
  }
  if (error || !restaurant) {
    return <div className="flex flex-col items-center justify-center py-20" data-id="element-303">
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 max-w-md text-center" data-id="element-304">
          <AlertCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" data-id="element-305" />
          <h2 className="text-xl font-bold text-white mb-2" data-id="element-306">
            Error de Configuración
          </h2>
          <p className="text-gray-400" data-id="element-307">
            {error || 'No se pudo identificar el restaurante.'}
          </p>
        </div>
      </div>;
  }
  const tabs: {
    id: SubTab;
    label: string;
  }[] = [{
    id: 'resumen',
    label: 'Resumen'
  }, {
    id: 'ingredientes',
    label: 'Ingredientes'
  }, {
    id: 'recetas',
    label: 'Recetas'
  }, {
    id: 'rentabilidad',
    label: 'Rentabilidad'
  }, {
    id: 'alertas',
    label: 'Alertas'
  }];
  return <div className="space-y-6 animate-fade-in" data-id="element-308">
      {/* Subtab Navigation */}
      <div className="bg-surface border border-gray-800 rounded-xl p-1 flex overflow-x-auto no-scrollbar" data-id="element-309">
        {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-1 text-center ${activeTab === tab.id ? 'bg-dorado/10 text-dorado border border-dorado/20 shadow-sm' : 'text-gray-400 hover:text-white hover:bg-surface-light border border-transparent'}`} data-id="element-310">
            {tab.label}
          </button>)}
      </div>

      {/* Content Area */}
      <div className="min-h-[500px]" data-id="element-311">
        {activeTab === 'resumen' && <InventoryDashboard restaurantId={restaurant.id} data-id="element-312" />}
        {activeTab === 'ingredientes' && <IngredientsTable restaurantId={restaurant.id} data-id="element-313" />}
        {activeTab === 'recetas' && <RecipesTable restaurantId={restaurant.id} data-id="element-314" />}
        {activeTab === 'rentabilidad' && <ProfitabilityTable restaurantId={restaurant.id} data-id="element-315" />}
        {activeTab === 'alertas' && <AlertsPanel restaurantId={restaurant.id} data-id="element-316" />}
      </div>
    </div>;
}